/**
 * S8 toolbar extensions (Tasks 8.9 / 8.10), order band 800–899: the view switch — "Open in Blob view ↗" in the location row (visible on a
 * blob-service root of an account whose hierarchical namespace is confirmed) and **New folder** (Data Lake view; F7: the UI
 * says "folder" in all three browser modes — the tooltip names the Data Lake directory it creates).
 * The switch opens / activates the sibling tab with an EXPLICIT view intent and the equivalent location, so the
 * default routing never sends the user back (D1, finding S8-09).
 */
import { ArrowUpRight, FolderPlus } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { ReasonButton } from '../../../../components/ui/ReasonButton';
import { STORAGE_READ_ONLY_REASON } from '../../../../core/engines/azureStorageRefusal';
import type { HnsPathRef } from '../../../../types/azureStorage';
import { useStorageDialogStore } from '../properties/dialogs/storageDialogStore';
import { trackStorageEvent } from '../services/storageAnalytics';
import { registerStorageToolbarExtension, TOOLBAR_OVERFLOW_PRIORITY, type StorageToolbarContext } from '../toolbar/toolbarExtensions';
import { StorageToolbarMenuItem } from '../toolbar/StorageToolbarMenuItem';
import { openStorageExplorerTab } from '../utils/openStorageExplorerTab';
import { siblingViewTarget } from './hnsView';

const accountHns = (ctx: StorageToolbarContext): string | undefined => (typeof ctx.capabilities.hns === 'string' ? ctx.capabilities.hns : undefined);
const tabIdOf = (ctx: StorageToolbarContext): string | undefined => (typeof ctx.capabilities.tabId === 'string' ? ctx.capabilities.tabId : undefined);

/** The directory a Data Lake tab is showing (its location), or `undefined` outside the Data Lake view. */
export function hnsLocationOf(ctx: Pick<StorageToolbarContext, 'mode' | 'resource'>): HnsPathRef | undefined {
  if (ctx.mode !== 'hns') return undefined;
  if (ctx.resource.kind === 'hnsPath' && ctx.resource.isDirectory) return ctx.resource;
  return ctx.resource.kind === 'container' ? { kind: 'hnsPath', fileSystem: ctx.resource.container, path: '', isDirectory: true } : undefined;
}

export const showsHnsViewSwitch = (ctx: StorageToolbarContext): boolean =>
  accountHns(ctx) === 'yes' && siblingViewTarget({ root: ctx.root, resource: ctx.resource, mode: ctx.mode }) !== undefined;

/**
 * UI/UX fix round A (review U27): the control opens (or activates) the other view's TAB, so it no longer looks like an
 * in-place toggle — the current view is a label, the other one a button "Open in Blob view ↗" — and the two tabs' titles
 * end in " · Data Lake" / " · Blob" (`storageViewTabTitle`).
 */
function HnsViewSwitch({ ctx }: { ctx: StorageToolbarContext }) {
  const current = ctx.mode === 'hns' ? 'data_lake' : 'blob';
  const other = current === 'data_lake' ? 'blob' : 'data_lake';
  const label = (view: 'data_lake' | 'blob') => (view === 'data_lake' ? 'Data Lake' : 'Blob');
  const testId = (view: 'data_lake' | 'blob') => `storage-hns-view-${view === 'data_lake' ? 'data-lake' : 'blob'}`;
  const open = () => {
    const target = siblingViewTarget({ root: ctx.root, resource: ctx.resource, mode: ctx.mode });
    if (!target) return;
    trackStorageEvent('ui:storage_hns_view', { view: other });
    openStorageExplorerTab({ connectionId: ctx.connectionId, root: target.root, resource: target.resource, viewIntent: target.viewIntent, source: 'view-switch' });
  };
  return (
    <div className="flex items-center gap-1" role="group" aria-label="View" data-testid="storage-hns-view-switch">
      {/* C1 (R5 / N2): the tab title already ends in " · Data Lake" / " · Blob", so the current view is named for
          screen readers only and the switch is just the button — it sits in the location row beside Hierarchy / Flat. */}
      <span className="sr-only" aria-current="true" data-testid={testId(current)}>{label(current)} view</span>
      <Button variant="ghost" size="xs" className="gap-1" onClick={open} title={`Open this location in the ${label(other)} view (in its own tab)`} data-testid={testId(other)}>
        Open in {label(other)} view <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
    </div>
  );
}

function useNewDirectoryAction(ctx: StorageToolbarContext): { reason?: string; open: () => void } {
  const parent = hnsLocationOf(ctx);
  // C1 (N9): a refused data role makes New folder unavailable with the reason, like Upload / Delete (round A, U12).
  const writeRefusal = typeof ctx.capabilities.writeRefusal === 'string' ? ctx.capabilities.writeRefusal : undefined;
  const reason = ctx.readOnly ? STORAGE_READ_ONLY_REASON : writeRefusal;
  const open = () => {
    if (!parent || reason) return;
    trackStorageEvent('toolbar:storage_new_directory');
    const tabId = tabIdOf(ctx);
    useStorageDialogStore.getState().openDialog({ kind: 'hns-new-directory', connectionId: ctx.connectionId, resource: parent, source: 'toolbar', hns: { origin: 'toolbar', ...(tabId ? { tabId } : {}) } });
  };
  return { reason, open };
}

function NewDirectoryButton({ ctx }: { ctx: StorageToolbarContext }) {
  const { reason, open } = useNewDirectoryAction(ctx);
  return (
    <ReasonButton variant="outline" size="xs" className="gap-1" reason={reason} title="Create a folder (a Data Lake directory) here" onClick={open} data-testid="storage-toolbar-new-directory">
      <FolderPlus className="h-3.5 w-3.5" /> New folder
    </ReasonButton>
  );
}

/** Its More-menu form when the action row runs out of width (C1 R5 / N2). */
function NewDirectoryMenuItem({ ctx }: { ctx: StorageToolbarContext }) {
  const { reason, open } = useNewDirectoryAction(ctx);
  return <StorageToolbarMenuItem icon={FolderPlus} label="New folder…" reason={reason} onSelect={open} testId="storage-toolbar-new-directory" />;
}

export function registerHnsToolbarExtensions(): () => void {
  const unregister = [
    // UI/UX fix round A (U7): the view switch is a view control (the action row's right side, order >= 900); New folder
    // takes the one New folder position of every storage kind.
    registerStorageToolbarExtension({ id: 's8-view-switch', placement: 'primary', order: 900, isVisible: showsHnsViewSwitch, render: (ctx) => <HnsViewSwitch ctx={ctx} /> }),
    registerStorageToolbarExtension({
      id: 's8-new-directory', placement: 'primary', order: 110, isVisible: (ctx) => hnsLocationOf(ctx) !== undefined, render: (ctx) => <NewDirectoryButton ctx={ctx} />,
      overflow: { priority: TOOLBAR_OVERFLOW_PRIORITY.newFolder, renderMenuItem: (ctx) => <NewDirectoryMenuItem ctx={ctx} /> },
    }),
  ];
  return () => unregister.forEach((dispose) => dispose());
}
